import {
  Avatar,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "@heroui/react";
import { ArrowDownTrayIcon, DocumentIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createCardView, getOpportunityCard } from "../../../services/api";
import {
  CardViewRequestDto,
  MarketplaceCardResponseDto,
  TemplateField,
  TemplateStep,
} from "../../../types/types";
import { formatDate } from "../../../utils/formatUtils";
import {
  formatPurseText,
  getCurrencySymbol,
} from "../../../utils/opportunityAndReadyroster";
import {
  formatEnumString,
  formatSportName,
  formatWeightClassName,
} from "../../../utils/utilMethods";
import {
  getOutcomePolicyFormValue,
  getOutcomeRegistryRows,
  OutcomeRegistryRow,
} from "../../../utils/outcomeRegistryHelpers";
import { RoleBasedActions } from "./roleBasedActions";

interface OpportunityViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string | number | null;
  activeTab: "all" | "myCards";
  onRefresh?: () => void;
}

const EMPTY_VALUE = "-";

type Translator = (key: string, options?: any) => string;

const toTitleCase = (str: string): string => {
  const normalized = String(str || "")
    .replace(/[_-]+/g, " ")
    .trim();

  const readableText = /^[A-Z0-9\s]+$/.test(normalized)
    ? normalized.toLowerCase()
    : normalized.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

  return readableText
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const flattenTranslationValues = (
  source: Record<string, unknown>,
  prefix = "",
): Record<string, string> => {
  return Object.entries(source).reduce(
    (acc: Record<string, string>, [key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (typeof value === "string") {
        acc[value] = fullKey;
      } else if (value && typeof value === "object") {
        Object.assign(
          acc,
          flattenTranslationValues(value as Record<string, unknown>, fullKey),
        );
      }

      return acc;
    },
    {},
  );
};

const isEnumLikeString = (value: string): boolean => {
  const trimmed = value.trim();
  return /^[A-Z0-9_]+$/.test(trimmed) || /^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)+$/.test(trimmed);
};

const shouldFormatChoiceValue = (value: string, key?: string): boolean =>
  isEnumLikeString(value) || (key ? /policy$/i.test(key) : false);

const formatChoiceText = (value: string, t?: Translator): string => {
  const trimmed = value.trim();

  if (t) {
    const key = `field_values.${trimmed.toLowerCase()}`;
    const translated = t(key, { defaultValue: "" });
    if (translated) return translated;
  }

  if (/^[A-Z0-9_]+$/.test(trimmed) || trimmed.includes("_")) {
    return formatEnumString(trimmed) || toTitleCase(trimmed);
  }
  return toTitleCase(trimmed);
};

const getFieldOptionLabel = (
  field: TemplateField | undefined,
  value: any,
  t?: Translator,
): string | null => {
  if (!field?.options?.length || value === null || value === undefined) return null;

  const option = field.options.find((item: any) => {
    const optionValue = item && typeof item === "object" ? item.value : item;
    return String(optionValue) === String(value);
  });

  if (!option) return null;
  if (typeof option === "object") {
    return option.label || formatChoiceText(String(option.value), t);
  }
  return formatChoiceText(String(option), t);
};

const shouldRenderNestedValue = (value: any, field?: TemplateField): boolean =>
  typeof value === "object" && value !== null && !(Array.isArray(value) && !!field?.options?.length);


const safe = (val: any): string => {
  if (val === null || val === undefined) return EMPTY_VALUE;
  if (typeof val === "string" && val.trim() === "") return EMPTY_VALUE;
  return String(val);
};

const getFirstValue = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const FILE_EXTENSION_PATTERN =
  /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf|csv|zip|rar|7z)(?:$|[?#])/i;

const getFileUrl = (value: any): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    return value.fileUrl || value.url || value.downloadUrl || "";
  }
  return "";
};

const isFileValue = (value: any): boolean => {
  const url = getFileUrl(value);
  return /^https?:\/\//i.test(url) && FILE_EXTENSION_PATTERN.test(url);
};

const getFileName = (value: any, fallback: string): string => {
  if (value && typeof value === "object" && (value.fileName || value.name)) {
    return value.fileName || value.name;
  }

  const url = getFileUrl(value);
  try {
    const pathname = new URL(url).pathname;
    const rawName = pathname.split("/").filter(Boolean).pop();
    return rawName ? decodeURIComponent(rawName) : fallback;
  } catch {
    const rawName = url.split("/").pop()?.split("?")[0];
    return rawName ? decodeURIComponent(rawName) : fallback;
  }
};

const getFileExtension = (fileName: string): string => {
  const extension = fileName.split(".").pop();
  return extension ? extension.toUpperCase() : "FILE";
};

const kindStyles: Record<string, string> = {
  bonus: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  expense: "bg-rose-50 text-rose-700 border border-rose-200",
  penalty: "bg-amber-50 text-amber-700 border border-amber-200",
};

const ID_FIELDS_WITH_NAME_SIBLING = new Set(["opponentFighterId"]);

// Some backend steps, like payout policies, do not send a fields array.
const getStepFields = (step: TemplateStep): TemplateField[] => {
  return Array.isArray((step as any).fields) ? (step as any).fields : [];
};

// New cards store payout policies under policies.outcomes.
// Older cards may still store WIN, LOSS, etc. directly under policies.
const getPolicyOutcomes = (stepData: any): Record<string, any> => {
  if (!stepData || typeof stepData !== "object") return {};
  if (
    stepData.outcomes &&
    typeof stepData.outcomes === "object" &&
    !Array.isArray(stepData.outcomes)
  ) {
    return stepData.outcomes;
  }
  return stepData;
};


const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="mb-3 flex items-center gap-3">
    <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
    <div className="h-px bg-gray-200 flex-1" />
  </div>
);

const InfoPair: React.FC<{
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}> = ({ label, value, wide }) => (
  <div className={wide ? "col-span-3 min-w-0" : "min-w-0"}>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-800 leading-snug break-words">{value ?? EMPTY_VALUE}</p>
  </div>
);


const FileValue: React.FC<{ label: string; value: any }> = ({ label, value }) => {
  const { t } = useTranslation("opportunities");
  const fileUrl = getFileUrl(value);
  const fileName = getFileName(value, label || t("document"));
  const extension = getFileExtension(fileName);

  const handleDownload = async () => {
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(t("download_failed"));

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error(t("download_failed"));
    }
  };

  return (
    <span className="flex min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white text-gray-600 ring-1 ring-gray-200">
        <DocumentIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-semibold text-gray-800" title={fileName}>
          {fileName}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {extension}
        </span>
      </span>
      <button
        type="button"
        onClick={handleDownload}
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100"
        title={t("download_file", { fileName })}
        aria-label={t("download_file", { fileName })}
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
      </button>
    </span>
  );
};

export const OpportunityViewModal: React.FC<OpportunityViewModalProps> = ({
  isOpen,
  onClose,
  cardId,
  activeTab,
  onRefresh,
}) => {
const [viewCard, setViewCard] = useState<MarketplaceCardResponseDto | null>(null);
const [loading, setLoading] = useState(false);
const { t, i18n } = useTranslation("opportunities");

const reverseTranslationMap = useMemo(() => {
  const enBundle =
    i18n.getResourceBundle("en", "opportunities") || {};
  const marketBundle = i18n.getResourceBundle("en", "marketPlaceCards") || {};

  return {
    ...flattenTranslationValues(enBundle),
    ...flattenTranslationValues(marketBundle),
  };
}, [i18n]);

const tr = (text?: string, fallback = ""): string => {
  if (!text) return fallback;

  const direct = t(text);
  if (direct !== text) return direct;

  const mappedKey = reverseTranslationMap[text];
  if (mappedKey) {
    const translated = t(mappedKey);
    if (translated !== mappedKey) return translated;

    const oppMapped = i18n.t(mappedKey, { ns: "opportunities" });
    if (oppMapped !== mappedKey) return oppMapped;

    const marketMapped = i18n.t(mappedKey, { ns: "marketPlaceCards" });
    if (marketMapped !== mappedKey) return marketMapped;
  }

  return text;
};

const navigate = useNavigate();

  const loginResponse = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("loginResponse") || "{}"); }
    catch { return {}; }
  }, []);
  const loggedProfileType = loginResponse?.profileType;

  const formData: any = viewCard?.opportunityCardResponse?.formData || {};
  const template = viewCard?.opportunityCardResponse?.templateJson || null;
  const currency = formData?.core?.currency || "";
  const manager = viewCard?.opportunityCardResponse?.manager;

  const getTemplateField = (fieldPath: string): TemplateField | undefined => {
    const [stepId, ...fieldParts] = fieldPath.split(".");
    if (!stepId || fieldParts.length === 0 || !Array.isArray(template?.steps)) {
      return undefined;
    }

    const step = template.steps.find(
      (item: TemplateStep) => item.stepId === stepId,
    );
    if (!step) return undefined;

    let fields = getStepFields(step as TemplateStep);
    let field: TemplateField | undefined;

    for (const fieldKey of fieldParts) {
      field = fields.find((item) => item.key === fieldKey);
      fields = Array.isArray((field as any)?.fields)
        ? ((field as any).fields as TemplateField[])
        : [];
    }

    return field;
  };

  const getTemplateFieldLabel = (
    fieldPath: string,
    fallback: string,
  ): string => getTemplateField(fieldPath)?.label || fallback;

  const getTemplateOptionLabel = (
    fieldPath: string,
    value: any,
  ): string | null => {
    const field = getTemplateField(fieldPath);
    return getFieldOptionLabel(field, value, t);
  };

  const getPolicyTranslation = (key: string, fallback: string): string =>
    t(`payout_policy.${key}`, { defaultValue: fallback });

  const getOutcomeLabel = (row: OutcomeRegistryRow, outcomeKey: string) =>
    t(`payout_outcome.${outcomeKey}`, {
      defaultValue: row.label || formatChoiceText(outcomeKey, t),
    });

  const getSelectionModeLabel = (value: any) => {
    if (!value) return EMPTY_VALUE;
    return t(`payout_selection_mode.${value}`, {
      defaultValue: formatChoiceText(String(value), t),
    });
  };

  const handleProfileNavigation = (profileId?: string | number | null) => {
    if (!profileId) return;
    navigate(`/profile?profileId=${profileId}`);
  };

  const formatValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined || value === "") return EMPTY_VALUE;

    if (isFileValue(value)) {
      return <FileValue label={key ? toTitleCase(key) : t("document")} value={value} />;
    }

    if (typeof value === "boolean") {
      return (
        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded border ${
          value
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-gray-100 text-gray-500 border-gray-200"
        }`}>
          {value ? t("yes") : t("no")}
        </span>
      );
    }

    if (typeof value === "number") {
      const isMonetary =
        key?.toLowerCase().includes("amount") ||
        key === "value" ||
        key?.toLowerCase().includes("purse");
      if (isMonetary) {
        return (
          <span className="font-semibold text-gray-900 tabular-nums">
            {formatPurseText(value)}&nbsp;{getCurrencySymbol(currency)}
          </span>
        );
      }
      return <span className="tabular-nums">{value}</span>;
    }

    if (typeof value === "string") {
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        try { return formatDate(value); } catch { return value; }
      }
      if (shouldFormatChoiceValue(value, key)) {
        if (key === "sport") return formatSportName(value) || formatEnumString(value) || toTitleCase(value);
        if (key === "weightClass") return formatWeightClassName(value) || formatEnumString(value) || toTitleCase(value);
        return formatChoiceText(value, t);
      }
      return value;
    }
    return String(value);
  };

  const formatFieldValue = (field: TemplateField | undefined, value: any): React.ReactNode => {
    if (Array.isArray(value) && field?.options?.length) {
      if (!value.length) return EMPTY_VALUE;
      return value
        .map((item) => getFieldOptionLabel(field, item, t) || formatChoiceText(String(item), t))
        .join(", ");
    }

    const optionLabel = getFieldOptionLabel(field, value, t);
    return optionLabel || formatValue(value, field?.key);
  };

  const renderNestedValue = (obj: any): React.ReactNode => {
    if (obj === null || obj === undefined) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
    if (isFileValue(obj)) {
      return <FileValue label={t("document")} value={obj} />;
    }

    if (Array.isArray(obj)) {
      if (!obj.length) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
      return (
        <div className="space-y-0.5">
          {obj.map((item, i) => (
            <div key={i} className="text-xs text-gray-700">
              {typeof item === "object" ? renderNestedValue(item) : formatValue(item)}
            </div>
          ))}
        </div>
      );
    }
    if (typeof obj === "object") {
      const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== "");
      if (!entries.length) return <span className="text-gray-400">{EMPTY_VALUE}</span>;
      return (
        <div className="space-y-0.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-1.5 text-xs flex-wrap">
              <span className="text-gray-500 font-medium shrink-0">{toTitleCase(k)}:</span>
              <span className="text-gray-700">
                {typeof v === "object" ? renderNestedValue(v) : formatValue(v as any, k)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return String(obj);
  };

  const buildDisplayFields = (
    fields: TemplateField[] | undefined | null,
    data: Record<string, any>
  ): Array<{ key: string; label: string; value: any; wide: boolean; field?: TemplateField }> => {
    const safeFields = Array.isArray(fields) ? fields : [];
    const result: Array<{ key: string; label: string; value: any; wide: boolean; field?: TemplateField }> = [];
    const handledKeys = new Set<string>();

    for (const field of safeFields) {
      if (ID_FIELDS_WITH_NAME_SIBLING.has(field.key)) {
        const nameKey = field.key.replace(/Id$/, "Name"); // e.g. opponentFighterId → opponentFighterName
        const nameValue = data[nameKey];
        if (nameValue !== undefined && nameValue !== null && nameValue !== "") {
          result.push({
            key: nameKey,
            label: field.label || toTitleCase(field.key.replace(/Id$/, "")),
            value: nameValue,
            wide: false,
            field,
          });
          handledKeys.add(field.key);
          handledKeys.add(nameKey);
        }
        continue;
      }
    }

    for (const field of safeFields) {
      if (handledKeys.has(field.key)) continue;

      if (field.visibleWhen) {
        const [ck, cv] = Object.entries(field.visibleWhen)[0];
        if (data[ck] !== cv) continue;
      }

      if (data[field.key] === undefined) continue;

      const value = data[field.key];

      if (field.fields && Array.isArray(field.fields)) {
        result.push({ key: field.key, label: field.label || toTitleCase(field.key), value, wide: true, field });
        handledKeys.add(field.key);
        continue;
      }

      const isWide = field.type === "textarea";

      result.push({ key: field.key, label: field.label || toTitleCase(field.key), value, wide: isWide, field });
      handledKeys.add(field.key);
    }

    const narrow = result.filter((f) => !f.wide);
    const wide = result.filter((f) => f.wide);
    return [...narrow, ...wide];
  };

  const renderStepFields = (fields: TemplateField[] | undefined | null, data: Record<string, any>) => {
    const safeFields = Array.isArray(fields) ? fields : [];
    const displayFields = buildDisplayFields(safeFields, data);

    const narrowFields = displayFields.filter((df) => !df.wide);
    const wideFields = displayFields.filter((df) => df.wide);

    const renderWideField = (df: { key: string; label: string; value: any; wide: boolean; field?: TemplateField }) => {
      const field = df.field || safeFields.find((f) => f.key === df.key);
      if (field?.fields && Array.isArray(field.fields)) {
        return (
          <div key={df.key}>
            <SectionHeader title={df.label} />
            <div className="grid grid-cols-3 gap-x-8 gap-y-4">
              {field.fields.map((sf) => (
                <InfoPair
                  key={sf.key}
                  label={tr(sf.label || toTitleCase(sf.key))}
                  value={formatFieldValue(sf, df.value?.[sf.key]) ?? EMPTY_VALUE}
                />
              ))}
            </div>
          </div>
        );
      }
      return (
        <div key={df.key}>
          <p className="text-xs text-gray-500 mb-0.5">{df.label}</p>
          <p className="text-sm font-medium text-gray-800 leading-snug whitespace-pre-wrap">
            {typeof df.value === "string" && df.value ? df.value : EMPTY_VALUE}
          </p>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {narrowFields.length > 0 && (
          <div className="grid grid-cols-3 gap-x-8 gap-y-4">
            {narrowFields.map((df) => (
              <InfoPair
                key={df.key}
                label={tr(df.label)}
                value={
                  shouldRenderNestedValue(df.value, df.field)
                    ? renderNestedValue(df.value)
                    : formatFieldValue(df.field, df.value)
                }
              />
            ))}
          </div>
        )}
        {wideFields.map((df) => renderWideField(df))}
      </div>
    );
  };

  const renderRepeatableStep = (step: TemplateStep, items: any[]) => {
    const fields = getStepFields(step);

    return (
      <div className="space-y-3">
        {items.map((item: any, idx: number) => {
          const visibleFields = fields.filter((field) => {
            if (["componentType", "kind", "mandatory"].includes(field.key)) return false;
            if (item[field.key] === undefined) return false;
            if (field.visibleWhen) {
              const [ck, cv] = Object.entries(field.visibleWhen)[0];
              return item[ck] === cv;
            }
            return true;
          });
          const componentLabel = item.componentType
            ? formatChoiceText(String(item.componentType), t)
            : `${step.title || "Item"} #${idx + 1}`;
          const kindLabel = item.kind ? formatChoiceText(String(item.kind), t) : "";

          return (
            <div key={idx} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-bold text-gray-500">
                    #{idx + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">
                      {componentLabel}
                    </p>
                    {kindLabel && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        {kindLabel}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {item.kind && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${
                      kindStyles[item.kind] || "bg-gray-100 text-gray-600 border-gray-200"
                    }`}>
                      {kindLabel}
                    </span>
                  )}
                  {item.mandatory !== undefined && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${
                      item.mandatory
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : "bg-gray-100 text-gray-500 border-gray-200"
                    }`}>
                      {item.mandatory ? t("mandatory") : t("optional")}
                    </span>
                  )}
                </div>
              </div>

              {fields.length === 0 ? (
                renderNestedValue(item)
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleFields.map((field) => (
                    <div key={field.key} className="rounded-md bg-gray-50 px-3 py-2">
                      <InfoPair
                        label={tr(field.label || toTitleCase(field.key))}
                        value={
                          shouldRenderNestedValue(item[field.key], field)
                            ? renderNestedValue(item[field.key])
                            : formatFieldValue(field, item[field.key])
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPayoutPolicies = (
    stepData: Record<string, any>,
    step: TemplateStep,
  ) => {
    const outcomes = getPolicyOutcomes(stepData);
    const outcomeRegistry = (step as any)?.outcomeRegistry;
    const registryRows = outcomeRegistry
      ? getOutcomeRegistryRows(outcomeRegistry)
      : [];
    const handledOutcomeKeys = new Set<string>();

    const registryOutcomeEntries = registryRows.map((row) => {
      handledOutcomeKeys.add(row.key);
      return {
        key: row.key,
        row,
        value: outcomes[row.key] || {},
      };
    });

    const extraOutcomeEntries = Object.entries(outcomes)
      .filter(
        ([key, value]) =>
          !handledOutcomeKeys.has(key) &&
          value &&
          typeof value === "object" &&
          !Array.isArray(value),
      )
      .map(([key, value]: [string, any]) => ({
        key,
        row: {
          key,
          group: "",
          label: value.label,
          baseLocked: value.baseLocked,
          baseIncluded: value.baseIncluded,
          componentsSelectable: true,
          selectionMode: value.selectionMode,
        } as OutcomeRegistryRow,
        value,
      }));

    const outcomeEntries = [...registryOutcomeEntries, ...extraOutcomeEntries];

    if (outcomeEntries.length === 0) {
      return <p className="text-sm text-gray-400">{EMPTY_VALUE}</p>;
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        {outcomeEntries.map(({ key, row, value }) => {
          const rowMeta: any = row || {};
          const registryMeta: any = outcomeRegistry || {};
          const policyValue = getOutcomePolicyFormValue(row, value);
          const baseLocked = !!policyValue.baseLocked;
          const baseIncluded = !!policyValue.baseIncluded;
          const modeValue = getFirstValue(
            value.selectionMode,
            row.selectionMode,
            value.appliedPayoutType,
            rowMeta.appliedPayoutType,
          );
          const modeText = getSelectionModeLabel(modeValue);
          const selectedComponents = Array.isArray(
            policyValue.selectedComponents,
          )
            ? policyValue.selectedComponents
            : [];
          const componentFieldPath =
            row.optionsFrom || registryMeta.optionsFrom || "components.componentType";
          const baseAmountLabel = getTemplateFieldLabel(
            "core.baseAmount",
            getPolicyTranslation("baseAmount", "Base Amount"),
          );
          const selectedComponentsLabel = String(
            getFirstValue(
              rowMeta.selectedComponentsLabel,
              rowMeta.selectedComponentsTitle,
              registryMeta.selectedComponentsLabel,
              registryMeta.selectedComponentsTitle,
              getPolicyTranslation("selectedComponents", "Selected Components"),
            ),
          );
          const modeLabel = String(
            getFirstValue(
              rowMeta.selectionModeLabel,
              rowMeta.modeLabel,
              registryMeta.selectionModeLabel,
              registryMeta.modeLabel,
              getPolicyTranslation("mode", "Mode"),
            ),
          );
          const baseLockedText = String(
            getFirstValue(
              baseLocked
                ? rowMeta.baseLockedText || registryMeta.baseLockedText
                : rowMeta.baseEditableText || registryMeta.baseEditableText,
              getPolicyTranslation(
                baseLocked ? "baseLocked" : "baseEditable",
                baseLocked ? "Base Locked" : "Base Editable",
              ),
            ),
          );
          const baseIncludedText = String(
            getFirstValue(
              baseIncluded
                ? rowMeta.baseIncludedText || registryMeta.baseIncludedText
                : rowMeta.baseNotIncludedText || registryMeta.baseNotIncludedText,
              getPolicyTranslation(
                baseIncluded ? "included" : "notIncluded",
                baseIncluded ? "Included" : "Not Included",
              ),
            ),
          );

          return (
            <div key={key} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {getOutcomeLabel(row, key)}
                  </p>
                  {modeValue && (
                    <p className="mt-0.5 text-xs text-gray-400">
                      {modeText}
                    </p>
                  )}
                </div>
                {policyValue.baseLocked !== undefined && (
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                    baseLocked
                      ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-gray-200 bg-white text-gray-500"
                  }`}>
                    {baseLockedText}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <InfoPair
                    label={baseAmountLabel}
                    value={baseIncludedText}
                  />
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2 sm:col-span-2">
                  <p className="text-xs text-gray-500 mb-1">
                    {selectedComponentsLabel}
                  </p>
                  {selectedComponents.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedComponents.map((item: any) => (
                        <span
                          key={String(item)}
                          className="rounded border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700"
                        >
                          {getTemplateOptionLabel(componentFieldPath, item) ||
                            formatChoiceText(String(item), t)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-800">{EMPTY_VALUE}</p>
                  )}
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2 sm:col-span-3">
                  <InfoPair label={modeLabel} value={modeText} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTemplateSteps = () => {
    if (!Array.isArray(template?.steps)) return null;
    const visibleSteps = template.steps.filter(
      (step: TemplateStep) => step.visible !== false
    );

    return (
      <div className="space-y-6 pb-1">
        {visibleSteps.map((step: TemplateStep) => {
          const stepData = formData[step.stepId];
          if (!stepData) return null;

          const stepFields = getStepFields(step);
          const isRepeatable = !!step.repeatable;
          const isNotes = step.stepId === "notes";
          const isPayoutPolicies =
            step.stepId === "policies" ||
            /payout\s*policies/i.test(String(step.title || ""));

          return (
            <div key={step.stepId}>
              <SectionHeader title={tr(step.title)} />
              {isPayoutPolicies ? (
                renderPayoutPolicies(stepData, step)
              ) : isRepeatable ? (
                Array.isArray(stepData) && stepData.length > 0
                  ? renderRepeatableStep(step, stepData)
                  : <p className="text-sm text-gray-400">{EMPTY_VALUE}</p>
              ) : isNotes ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {stepData[stepFields[0]?.key] || EMPTY_VALUE}
                </p>
              ) : (
                renderStepFields(stepFields, stepData)
              )}
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (isOpen && cardId) {
      const loadCard = async () => {
        setLoading(true);
        try {
          const response = await getOpportunityCard(cardId);
          setViewCard(response?.data ? response.data : response);
        } catch {
          toast.error(t("failed_to_load_opportunity_details"));
        } finally {
          setLoading(false);
        }
      };
      loadCard();
      trackGeoLocation(cardId);
    } else {
      setViewCard(null);
    }
  }, [isOpen, cardId]);

  const trackGeoLocation = async (viewedCardId?: string | number) => {
    if (!viewedCardId) return;
    try {
      const storageKey = `lastViewed_${viewedCardId}`;
      const lastViewed = localStorage.getItem(storageKey);
      if (lastViewed && Date.now() - Number(lastViewed) < 5 * 60 * 1000) return;
      const geoRes = await fetch("https://ipapi.co/json");
      const geoData = await geoRes.json();
      const userJson = localStorage.getItem("loginResponse");
      const user = userJson ? JSON.parse(userJson) : null;
      if (!user?.username) return;
      const dto: CardViewRequestDto = {
        marketplaceCardId: viewedCardId,
        informationRequestDto: {
          ipAddress: geoData.ip,
          countryCode: geoData.country_code,
          city: geoData.city,
          browserInfo: navigator.userAgent,
          sourcePage: window.location.pathname,
        },
      };
      await createCardView(user.username, dto);
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (err) {
      console.error("Geo tracking failed", err);
    }
  };

  const renderManagerPanel = () => (
    <div className="bg-rose-50 border border-gray-200 rounded-xl p-5 space-y-4 self-start">
      <button
        type="button"
        onClick={() => handleProfileNavigation(manager?.id)}
        disabled={!manager?.id}
        className="flex w-full items-center gap-3 rounded-lg text-left transition hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-300 disabled:cursor-default disabled:hover:bg-transparent"
      >
        <Avatar
          src={manager?.profileImage || undefined}
          name={
            !manager?.profileImage && (manager?.firstName || manager?.lastName)
              ? `${(manager?.firstName || "").charAt(0)}${(manager?.lastName || "").charAt(0)}`
              : "?"
          }
          size="lg"
          className="bg-gray-500 text-white shrink-0 font-bold"
        />
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-gray-900 leading-tight truncate">
            {safe(manager?.firstName)} {safe(manager?.lastName)}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {manager?.managerType ? toTitleCase(manager.managerType.toLowerCase()) : EMPTY_VALUE}
          </p>
        </div>
      </button>

      <div className="h-px bg-gray-200" />

      <div>
        <SectionHeader title={t("personal_details")} />
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <InfoPair label={t("organization_name")} value={safe(manager?.organizationName)} />
          <InfoPair label={t("nationality")} value={safe(manager?.nationality)} />
          <InfoPair
            label={t("gender")}
            value={manager?.gender ? toTitleCase(manager.gender.toLowerCase()) : EMPTY_VALUE}
          />
          <InfoPair label={t("residence_country")} value={safe(manager?.residenceCountry)} />
        </div>
      </div>

      {loggedProfileType === "ADMIN" && (
        <>
          <div className="h-px bg-gray-200" />
          <div>
            <SectionHeader title={t("contact_details")} />
            <div className="space-y-3">
              <InfoPair
                label={t("email")}
                value={<span className="break-all text-xs">{safe(manager?.email)}</span>}
              />
              <InfoPair label={t("phone")} value={safe(manager?.phoneNumber)} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      disableAnimation
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[92vh]",
        wrapper: "overflow-hidden",
      }}
    >
      <ModalContent
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col max-h-[92vh] bg-white rounded-xl"
      >
        <ModalHeader className="border-b border-gray-200 px-6 py-4 flex-shrink-0 flex items-center">
          <span className="text-lg font-semibold text-gray-900 leading-tight">
            {t("opportunity_details")}
          </span>
        </ModalHeader>

        <ModalBody className="px-6 py-5 flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Spinner size="lg" color="primary" label={t("loading")} />
            </div>
          ) : viewCard ? (
            <div className="grid grid-cols-12 gap-6 h-full overflow-hidden items-start">
              <div className="col-span-4 overflow-y-auto max-h-full pr-1">
                {renderManagerPanel()}
              </div>

              <div className="col-span-1 flex justify-center self-stretch pointer-events-none">
                <div className="w-px bg-gray-200 h-full" />
              </div>

              <div className="col-span-7 h-full overflow-y-auto pl-1 pr-1">
                {renderTemplateSteps()}
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-10">{t("no_data")}</p>
          )}
        </ModalBody>

        {!loading && viewCard && (
          <div className="border-t border-gray-200 bg-white flex-shrink-0">
            <RoleBasedActions
              viewCard={viewCard}
              activeTab={activeTab}
              onClose={onClose}
              onRefresh={onRefresh}
            />
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default OpportunityViewModal;